/* global __APP_VERSION__, __GIT_HASH__ */
/**
 * Bottom Navigation
 * Fixed 5-tab navigation footer for the mobile layout. Routes without a tab
 * (launch, create season, docs, legal, admin) live in the SystemMenu.
 *
 * It publishes its rendered height (safe-area padding included) as the CSS
 * variable --bottom-nav-height, so the shell's <main> and fixed bars (e.g. the
 * token page's buy button) sit above it instead of guessing.
 */

import { useLayoutEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Ticket, TrendingUp, Coins, Wallet, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSafeArea } from "@/hooks/useSafeArea";

export const BottomNav = ({ className = "" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const safeArea = useSafeArea();
  const { t } = useTranslation(["navigation"]);
  const navRef = useRef(null);

  useLayoutEffect(() => {
    const nav = navRef.current;
    const root = document.documentElement;
    if (!nav) return undefined;
    const publish = () =>
      root.style.setProperty("--bottom-nav-height", `${nav.offsetHeight}px`);
    publish();
    const observer =
      typeof ResizeObserver === "function" ? new ResizeObserver(publish) : null;
    observer?.observe(nav);
    return () => {
      observer?.disconnect();
      root.style.removeProperty("--bottom-nav-height");
    };
  }, []);

  const tabs = [
    {
      id: "raffles",
      label: t("navigation:raffles"),
      icon: Ticket,
      path: "/raffles",
    },
    {
      id: "infofi",
      label: t("navigation:markets"),
      icon: TrendingUp,
      path: "/markets",
    },
    {
      id: "tokens",
      label: t("navigation:tokens"),
      icon: Coins,
      path: "/tokens",
    },
    {
      id: "portfolio",
      label: t("navigation:portfolio"),
      icon: Wallet,
      path: "/portfolio",
    },
    {
      id: "ranking",
      label: t("navigation:leaderboard"),
      icon: Trophy,
      path: "/leaderboard",
    },
  ];

  const getActiveTab = () => {
    const path = location.pathname;
    if (
      path.startsWith("/raffles") ||
      path.startsWith("/raffle/") ||
      path.startsWith("/create-season")
    )
      return "raffles";
    if (path.startsWith("/markets") || path.startsWith("/market/"))
      return "infofi";
    if (path === "/tokens" || path.startsWith("/tokens/") || path === "/launch")
      return "tokens";
    if (path.startsWith("/portfolio")) return "portfolio";
    if (path.startsWith("/leaderboard") || path.startsWith("/users"))
      return "ranking";
    // Home and the menu-only pages (docs, legal, admin) have no tab.
    return null;
  };

  const activeTab = getActiveTab();

  const version =
    typeof __APP_VERSION__ !== "undefined" &&
    typeof __GIT_HASH__ !== "undefined"
      ? `v${__APP_VERSION__}-${__GIT_HASH__}`
      : "dev";

  return (
    <nav
      ref={navRef}
      className={`fixed bottom-0 left-0 right-0 bg-background border-t border-border/20 ${className}`}
      style={{
        paddingBottom: `max(${safeArea.bottom}px, 8px)`,
      }}
    >
      <div className="max-w-screen-sm mx-auto">
        {/* Navigation Buttons */}
        <div className="grid grid-cols-5 gap-1.5 px-1.5 pt-3">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <Button
                key={tab.id}
                variant={isActive ? "default" : "outline"}
                onClick={() => navigate(tab.path)}
                aria-current={isActive ? "page" : undefined}
                className={`flex flex-col items-center justify-center gap-1 h-auto py-2 px-1 rounded-lg ${isActive ? "shadow-lg shadow-primary/30" : "opacity-60"}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-[10px] font-medium leading-tight">
                  {tab.label}
                </span>
              </Button>
            );
          })}
        </div>

        {/* Copyright and Version */}
        <div className="flex items-center justify-center gap-2 pt-1 pb-1">
          <p className="text-[9px] text-muted-foreground/70">
            &copy; {new Date().getFullYear()} SecondOrder.fun
          </p>
          <span className="text-[8px] text-muted-foreground/50">{version}</span>
        </div>
      </div>
    </nav>
  );
};

BottomNav.propTypes = {
  className: PropTypes.string,
};

export default BottomNav;
